import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Chip,
  Typography, IconButton, FormControlLabel, Switch, Checkbox, Radio, RadioGroup,
  Autocomplete, Alert, Snackbar, CircularProgress, Tooltip, Divider,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import useIsMobile from '../hooks/useIsMobile';
import {
  AlerteRegle, AlerteRegleInput, Apercu, ModeDeclenchement, StatutCandidat, VisaCatalogue,
  parseTitres, listerVisasCatalogue, creerAlerteRegle, majAlerteRegle, apercuAlerteRegle, TYPE_MARQUEUR_ARRIVEE_INDE
} from '../services/alertesReglesServices';

interface Props {
  open: boolean;
  regle: AlerteRegle | null; // null = création
  onClose: () => void;
  onSaved: () => void;
}

const statutCandidatChip: Record<StatutCandidat, { label: string; color: 'warning' | 'success' | 'default' | 'error' }> = {
  a_envoyer: { label: 'À alerter', color: 'warning' },
  deja_alerte: { label: 'Déjà alerté', color: 'success' },
  a_venir: { label: 'À venir', color: 'default' },
  depasse: { label: 'Fenêtre passée', color: 'error' },
  illisible: { label: 'Date illisible', color: 'error' },
};

// Message d'une erreur axios (400 : { message, erreurs: [] } ; 503 : { message })
const messageErreur = (e: unknown, defaut: string): string => {
  if (axios.isAxiosError(e) && e.response && e.response.data) {
    const data = e.response.data as { message?: string; erreurs?: string[] };
    if (Array.isArray(data.erreurs) && data.erreurs.length) return data.erreurs.join(' ');
    if (data.message) return data.message;
  }
  return defaut;
};

const AlerteRegleDialog: React.FC<Props> = ({ open, regle, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [libelle, setLibelle] = useState('');
  const [titres, setTitres] = useState<string[]>([]);
  const [motifTous, setMotifTous] = useState('');
  const [motifUnParmi, setMotifUnParmi] = useState('');
  const [mode, setMode] = useState<ModeDeclenchement>('fenetre');
  const [delai, setDelai] = useState('4');
  const [ecardNonFaite, setEcardNonFaite] = useState(false);
  const [note, setNote] = useState('');
  const [actif, setActif] = useState(true);
  const [visas, setVisas] = useState<VisaCatalogue[]>([]);
  const [dateSimulee, setDateSimulee] = useState('');
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [apercuLoading, setApercuLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const catalogueCharge = useRef(false);

  // Initialisation du formulaire à chaque ouverture (défauts : fenêtre, 4 j, e-card non cochée, actif)
  useEffect(() => {
    if (!open) return;
    if (regle) {
      setLibelle(regle.libelle || '');
      setTitres(parseTitres(regle.visas_titres));
      setMotifTous(regle.motif_tous || '');
      setMotifUnParmi(regle.motif_un_parmi || '');
      setMode(regle.mode_declenchement === 'exact' ? 'exact' : 'fenetre');
      setDelai(String(regle.delai_jours));
      setEcardNonFaite(Number(regle.ecard_non_faite) === 1);
      setNote(regle.note || '');
      setActif(Number(regle.actif) === 1);
    } else {
      setLibelle('');
      setTitres([]);
      setMotifTous('');
      setMotifUnParmi('');
      setMode('fenetre');
      setDelai('4');
      setEcardNonFaite(false);
      setNote('');
      setActif(true);
    }
    setDateSimulee('');
    setApercu(null);
  }, [regle, open]);

  // Catalogue des visas chargé une seule fois, à la première ouverture
  useEffect(() => {
    if (!open || catalogueCharge.current) return;
    catalogueCharge.current = true;
    listerVisasCatalogue()
      .then(setVisas)
      .catch((e) => {
        console.error('Erreur chargement catalogue visas', e);
        catalogueCharge.current = false;
        setToast({ msg: 'Chargement du catalogue des visas impossible', sev: 'error' });
      });
  }, [open]);

  // Options : titres non vides, dédoublonnées par titre (première occurrence), re-triées par pays puis titre
  // (GET /visas trie par titre ; MUI exige des options triées par groupe pour groupBy)
  const options = useMemo(() => {
    const vus = new Set<string>();
    const uniques: VisaCatalogue[] = [];
    visas.forEach((v) => {
      if (!v.titre || vus.has(v.titre)) return;
      vus.add(v.titre);
      uniques.push(v);
    });
    return uniques.sort(
      (a, b) => a.libelle_pays.localeCompare(b.libelle_pays) || (a.titre ?? '').localeCompare(b.titre ?? '')
    );
  }, [visas]);

  const valeur = useMemo(() => options.filter((v) => titres.includes(v.titre ?? '')), [options, titres]);

  // Titres stockés mais absents du catalogue (visa renommé) : conservés, retirables volontairement
  const orphelins = useMemo(
    () => (visas.length ? titres.filter((t) => !options.some((v) => v.titre === t)) : []),
    [visas, options, titres]
  );

  const delaiNum = Number(delai);
  const cibleVide = !(titres.length || motifTous.trim() || motifUnParmi.trim());
  const chevrons = /[<>]/.test(libelle + note + motifTous + motifUnParmi);
  const delaiOk = delai.trim() !== '' && Number.isInteger(delaiNum) && delaiNum >= 0 && delaiNum <= 365;
  const valide = !!libelle.trim() && !chevrons && !cibleVide && delaiOk;

  const form = (): AlerteRegleInput => ({
    libelle: libelle.trim(),
    visas_titres: titres,
    motif_tous: motifTous.trim(),
    motif_un_parmi: motifUnParmi.trim(),
    mode_declenchement: mode,
    delai_jours: delaiNum,
    ecard_non_faite: ecardNonFaite,
    note: note.trim(),
    actif,
  });

  const handleTester = async () => {
    if (!valide) return;
    setApercuLoading(true);
    try {
      const f = form();
      const r = await apercuAlerteRegle({
        id: regle ? regle.id : undefined,
        libelle: f.libelle,
        visas_titres: f.visas_titres,
        motif_tous: f.motif_tous,
        motif_un_parmi: f.motif_un_parmi,
        mode_declenchement: f.mode_declenchement,
        delai_jours: f.delai_jours,
        ecard_non_faite: f.ecard_non_faite,
        date_simulee: dateSimulee || undefined,
      });
      setApercu(r);
    } catch (e) {
      console.error('Erreur aperçu règle', e);
      setToast({ msg: messageErreur(e, "Erreur lors du test de la règle"), sev: 'error' });
    } finally {
      setApercuLoading(false);
    }
  };

  const handleSave = async () => {
    if (!valide) return;
    setSaving(true);
    try {
      if (regle) {
        await majAlerteRegle(regle.id, form());
      } else {
        await creerAlerteRegle(form());
      }
      setToast({ msg: 'Règle enregistrée', sev: 'success' });
      onSaved();
      onClose();
    } catch (e) {
      console.error('Erreur enregistrement règle', e);
      setToast({ msg: messageErreur(e, "Erreur lors de l'enregistrement"), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Box sx={{ flexGrow: 1 }}>{regle ? 'Modifier la règle' : 'Nouvelle règle'}</Box>
          <FormControlLabel
            control={<Switch checked={actif} onChange={(e) => setActif(e.target.checked)} />}
            label={actif ? 'Actif' : 'Désactivé'}
          />
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 1. Nom */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Nom</Typography>
            <TextField
              label="Nom de la règle"
              required
              fullWidth
              size="small"
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              error={/[<>]/.test(libelle)}
              helperText={/[<>]/.test(libelle) ? 'Les caractères < et > sont interdits' : 'Apparaît dans le sujet du mail et dans le dossier'}
            />
          </Box>

          {/* 2. Quels dossiers ? */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Quels dossiers ?</Typography>
            <Autocomplete
              multiple
              size="small"
              options={options}
              value={valeur}
              groupBy={(v) => v.libelle_pays}
              getOptionLabel={(v) => v.titre ?? ''}
              isOptionEqualToValue={(a, b) => a.titre === b.titre}
              renderOption={(props, v) => (
                <li {...props} key={v.titre ?? ''}>{v.titre}</li>
              )}
              onChange={(_, vals) => {
                const conserves = titres.filter((t) => !options.some((v) => v.titre === t));
                setTitres([...conserves, ...vals.map((v) => v.titre ?? '')]);
              }}
              ChipProps={{ size: 'small' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visas du catalogue"
                  helperText="Correspondance exacte avec le libellé du dossier (insensible à la casse/accents). Un visa renommé dans Visas n'est plus reconnu pour les anciens dossiers."
                />
              )}
            />
            {orphelins.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {orphelins.map((t) => (
                  <Chip
                    key={t}
                    size="small"
                    color="warning"
                    label={`“${t}” introuvable dans le catalogue`}
                    onDelete={() => setTitres((prev) => prev.filter((x) => x !== t))}
                  />
                ))}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 1 }}>
              …et/ou par mots-clés dans le libellé du visa (attrape aussi les dossiers hors catalogue : formulaires
              externes « eVisa Inde … », dossiers créés à la main = nom de pays)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Mots-clés tous obligatoires"
                placeholder="inde"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                value={motifTous}
                onChange={(e) => setMotifTous(e.target.value)}
                error={/[<>]/.test(motifTous)}
                helperText={/[<>]/.test(motifTous) ? 'Les caractères < et > sont interdits' : 'Séparés par des virgules'}
              />
              <TextField
                label="Au moins un de ces mots-clés"
                placeholder="evisa, ecard"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                value={motifUnParmi}
                onChange={(e) => setMotifUnParmi(e.target.value)}
                error={/[<>]/.test(motifUnParmi)}
                helperText={/[<>]/.test(motifUnParmi) ? 'Les caractères < et > sont interdits' : 'Séparés par des virgules'}
              />
            </Box>
            {cibleVide && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                Choisissez au moins un visa du catalogue ou un mot-clé.
              </Typography>
            )}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={ecardNonFaite} onChange={(e) => setEcardNonFaite(e.target.checked)} />}
              label="Seulement si l'e-card n'est pas encore faite (état e-card ≠ traité)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Inclut les dossiers à l'état e-card vide et « A traiter » — plus large que le bouton « marquer à traiter »
              de la liste, qui ne prend que l'état vide.
            </Typography>
          </Box>

          {/* 3. Quand ? */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Quand ?</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value === 'exact' ? 'exact' : 'fenetre')} sx={{ flex: 1, minWidth: 260 }}>
                <FormControlLabel
                  value="fenetre"
                  control={<Radio size="small" />}
                  label={
                    <span>
                      Départ dans les <strong>{delai || 'N'}</strong> prochains jours — recommandé : chaque dossier est alerté
                      une seule fois dès qu'il entre dans la fenêtre (rattrape les dossiers créés tard et les règles créées après coup)
                    </span>
                  }
                />
                <FormControlLabel
                  value="exact"
                  control={<Radio size="small" />}
                  label={
                    <span>
                      Départ dans exactement <strong>{delai || 'N'}</strong> jours — le dossier doit exister ce jour-là,
                      sinon il n'est jamais alerté
                    </span>
                  }
                />
              </RadioGroup>
              <TextField
                type="number"
                label="N (jours)"
                size="small"
                inputProps={{ min: 0, max: 365 }}
                sx={{ width: 120 }}
                value={delai}
                onChange={(e) => setDelai(e.target.value)}
                error={!delaiOk}
                helperText={delaiOk ? ' ' : 'Entier entre 0 et 365'}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Référence : la date de départ saisie dans le dossier (demandes.date_depart). Pour Singapour/Malaisie eCard,
              cette colonne contient la date d'ARRIVÉE. Togo non couvert (date de retour dans le json.txt). Dossiers exclus
              d'office : Annulé/Refusé, remboursé/annulé, sans date (les dossiers archivés sont inclus). Une passe a lieu
              toutes les heures, 24 h/24.
            </Typography>
            {regle && regle.type_marqueur === TYPE_MARQUEUR_ARRIVEE_INDE && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Règle e-cards Inde : le J-n est calculé sur la <strong>date d'arrivée en Inde</strong> saisie dans le formulaire
                quand elle est renseignée, sur la date de départ sinon (réglage fixe, propre à cette règle).
              </Alert>
            )}
          </Box>

          {/* 4. Message */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Message</Typography>
            <TextField
              label="Phrase ajoutée au mail (optionnel)"
              multiline
              minRows={2}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              error={/[<>]/.test(note)}
              helperText={/[<>]/.test(note) ? 'Les caractères < et > sont interdits' : `${note.length}/500`}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              Un seul mail récapitulatif est envoyé à contact@visamonde.com par passe. Le sujet et la mise en forme se
              modifient dans Mails → « Alerte interne — dossiers au départ proche ». Le dossier garde une trace dans sa
              liste d'alertes.
            </Alert>
          </Box>

          {/* 5. Tester */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Tester</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                type="date"
                label="Simuler à la date (optionnel)"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dateSimulee}
                onChange={(e) => setDateSimulee(e.target.value)}
              />
              <Button
                variant="outlined"
                startIcon={apercuLoading ? <CircularProgress size={16} /> : <VisibilityIcon />}
                disabled={!valide || apercuLoading}
                onClick={handleTester}
              >
                Tester (sans envoi)
              </Button>
              <Typography variant="caption" color="text.secondary">Aperçu sans envoi ni marquage</Typography>
            </Box>

            {apercu && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Référence : {apercu.date_reference}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  <Chip size="small" color="primary" label={`${apercu.nb_concernes} dossiers actifs concernés`} />
                  <Chip
                    size="small"
                    color={apercu.nb_a_envoyer > 0 ? 'warning' : 'default'}
                    label={`${apercu.nb_a_envoyer} à alerter à la prochaine passe`}
                  />
                  <Chip size="small" label={`${apercu.nb_deja_alertes} déjà alertés`} />
                  <Chip size="small" label={`${apercu.nb_a_venir} à venir`} />
                  <Chip size="small" label={`${apercu.nb_depasses} fenêtre passée`} />
                  <Tooltip title={apercu.illisibles.length ? apercu.illisibles.map((i) => `#${i.id} (${i.date_depart})`).join(', ') : ''}>
                    <Chip
                      size="small"
                      color={apercu.illisibles.length > 0 ? 'error' : 'default'}
                      label={`${apercu.illisibles.length} dates illisibles`}
                    />
                  </Tooltip>
                </Box>
                {apercu.dry_run && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Le moteur est en mode simulation : aucun mail ne partira tant que le dry-run est actif.
                  </Alert>
                )}
                {apercu.template.actif === 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Le mail « Alerte interne — dossiers au départ proche » est désactivé dans Mails : aucune alerte ne partira.
                  </Alert>
                )}
                {apercu.candidats.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>N°</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Réf.</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Visa</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Départ</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>J-n</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apercu.candidats.map((c) => (
                          <TableRow key={c.id} hover>
                            <TableCell>#{c.id}</TableCell>
                            <TableCell>{c.chemin || '—'}</TableCell>
                            <TableCell>{c.libelle_visa}</TableCell>
                            <TableCell>
                              {c.date_depart}
                              {c.date_reference === 'arrivee_inde' && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  arrivée en Inde {c.date_ref}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{c.jours === null ? '—' : `J-${c.jours}`}</TableCell>
                            <TableCell>
                              <Chip size="small" color={statutCandidatChip[c.statut].color} label={statutCandidatChip[c.statut].label} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Aucun dossier actif ne correspond à cette règle.</Typography>
                )}
              </Box>
            )}
          </Box>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Le compteur se rafraîchit au clic sur « Tester » et au chargement de la liste.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!valide || saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default AlerteRegleDialog;
